import type { Distance, SpatialRectangle } from "@pdfking/math"
import { Floor, Scale, Subtract, Total } from "@pdfking/math"
import { ZERO_DISTANCE } from "@pdfking/math/constants"
import { Range } from "@pdfking/tfunc"

type Box = SpatialRectangle<Distance>

/** The boxes a design cuts a page's boxes into: a strip off an edge of `box`
 * and what is left of it past a `gap`; or a grid of it. */
export const Slice = {
  /** `box` right of `x`, `gap` past it */
  after: (box: Box, x: Distance, gap: Distance = ZERO_DISTANCE): Box => ({
    ...box,
    width: Subtract(Total(box.x, box.width), Total(x, gap)),
    x: Total(x, gap),
  }),
  /** `box` up to `x`, `gap` short of it */
  before: (box: Box, x: Distance, gap: Distance = ZERO_DISTANCE): Box => ({
    ...box,
    width: Subtract(x, Total(box.x, gap)),
  }),
  /** `box`'s foot `height` tall, and what is left over it */
  bottom: (box: Box, height: Distance, gap: Distance = ZERO_DISTANCE): [Box, Box] => [
    { ...box, height },
    { ...box, height: Subtract(box.height, Total(height, gap)), y: Total(box.y, height, gap) },
  ],
  /** `count` boxes `columns` across `box` and `rows` down, `gap` apart, row
   * by row from its top left */
  grid: (box: Box, columns: number, rows: number, gap: Distance, count = columns * rows): Box[] => {
    const width = Scale(Subtract(box.width, Scale(gap, columns - 1)), 1 / columns)
    const height = Scale(Subtract(box.height, Scale(gap, rows - 1)), 1 / rows)
    return [...Range(count)].map(i => ({
      height,
      width,
      x: Total(box.x, Scale(Total(width, gap), i % columns)),
      y: Total(box.y, Scale(Total(height, gap), rows - 1 - Floor(i / columns))),
    }))
  },
  /** `box`'s left `width` wide, and what is left right of it */
  left: (box: Box, width: Distance, gap: Distance = ZERO_DISTANCE): [Box, Box] => [
    { ...box, width },
    { ...box, width: Subtract(box.width, Total(width, gap)), x: Total(box.x, width, gap) },
  ],
  /** `box` over `y`, `gap` past it */
  over: (box: Box, y: Distance, gap: Distance = ZERO_DISTANCE): Box => ({
    ...box,
    height: Subtract(Total(box.y, box.height), Total(y, gap)),
    y: Total(y, gap),
  }),
  /** `box`'s right `width` wide, and what is left left of it */
  right: (box: Box, width: Distance, gap: Distance = ZERO_DISTANCE): [Box, Box] => [
    { ...box, width, x: Subtract(Total(box.x, box.width), width) },
    { ...box, width: Subtract(box.width, Total(width, gap)) },
  ],
  /** `box`'s top `height` tall, and what is left under it */
  top: (box: Box, height: Distance, gap: Distance = ZERO_DISTANCE): [Box, Box] => [
    { ...box, height, y: Subtract(Total(box.y, box.height), height) },
    { ...box, height: Subtract(box.height, Total(height, gap)) },
  ],
  /** `box` up to `y`, `gap` short of it */
  under: (box: Box, y: Distance, gap: Distance = ZERO_DISTANCE): Box => ({
    ...box,
    height: Subtract(y, Total(box.y, gap)),
  }),
}
